import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Typography, InputAdornment, MenuItem, CircularProgress } from '@mui/material';
import {
  FaIdCard, FaUniversity, FaCalendarAlt, FaCalendarCheck, FaBackward,
  FaMapMarkedAlt, FaCity, FaGlobeAsia, FaForward, FaGlobe, FaEnvelope, FaFileUpload, FaTimes
} from 'react-icons/fa';
import './StepHighSchool.css';
import PopupModel from '../../popup-model/PopupModel';
import IndianStatesSelect from '../ListOfStates/IndianStates';
import { saveHighSchoolData, getHighSchoolData } from '../../../services/candidate/api';
import { useNavigate } from 'react-router-dom';
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";


const qualifications = [
  { value: 'SSC', label: 'Secondary School Certificate (SSC)' },
  { value: 'CBSE', label: 'Central Board of Secondary Education (CBSE)' },
  { value: 'ICSE', label: 'Indian Certificate of Secondary Education (ICSE)' }
];

const getFileNameFromUrl = (url) => {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    return decodeURIComponent(pathname.split('/').pop());
  } catch {
    return null;
  }
};

const StepHighSchool = ({ onNext, onBack }) => {
  const [form, setForm] = useState({
    enrollmentNumber: '',
    instituteName: '',
    qualification: '',
    enrolledYear: '',
    passingYear: '',
    address: '',
    city: '',
    stateRegionProvince: '',
    country: '',
    zipCode: '',
  });

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const certificateFileRef = useRef(null);
  const navigate = useNavigate();


  const getDateValue = (dateStr) => {
    if (!dateStr || !dateStr.includes('-')) return 0;
    const [month, year] = dateStr.split('-').map(Number);
    return year * 12 + month;
  };

  const validateField = (name, value) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const datePattern = /^(\d{2})-(\d{4})$/;
    let error = '';

    switch (name) {
      case 'enrollmentNumber':
        if (!value) error = 'Enrollment number is required';
        else if (!/^[a-zA-Z0-9]{1,20}$/.test(value)) error = 'Enrollment number must be alphanumeric and up to 20 characters';
        break;

       case 'instituteName':
        if (!value) error = 'InstituteName is required';
        else if (!/^[A-Za-z .'-]{1,100}$/.test(value)) 
        error = 'Institute name must contain only alphabets, spaces, dots, or hyphens (1-100 characters)';
        break;

      case 'qualification':
        if (!value) error = 'Qualification is required';
        break;

      case 'enrolledYear':
      case 'passingYear':
        if (!value) {
          error = `${name === 'enrolledYear' ? 'Enrolled' : 'Passing'} year is required`;
        } else if (!datePattern.test(value)) {
          error = 'Must be in MM-YYYY format';
        } else { 
          const [month, year] = value.split('-').map(Number);
          if (month < 1 || month > 12) {
            error = 'Month must be between 01 and 12';
          } else if (year < 1900) {
            error = 'Year must be after 1900';
          } else if (year > currentYear || (year === currentYear && month > currentMonth)) {
            error = 'Cannot be in the future';
          }
        }
        break;

      case 'city':
        if (!value) error = 'City is required';
        else if (!/^[A-Za-z ]{1,20}$/.test(value)) error = 'City must contain only alphabets (1-20 characters)';
        break;

      case 'stateRegionProvince':
        if (!value) error = 'State is required';
        break;

      case 'country':
        if (!value) error = 'Country is required';
        break;

      case 'zipCode':
        if (value && !/^\d{6}$/.test(value)) {
          error = 'Zip code must be exactly 6 digits';
        }
        break;

      case 'certificate':
        if (!value) error = 'Certificate file is required';
        else if (value instanceof File) {
          if (value.size > 5 * 1024 * 1024) error = 'File must be less than 5MB';
          else if (!['image/jpeg', 'image/png', 'application/pdf'].includes(value.type)) {
            error = 'File must be PDF or image (JPEG/PNG)';
          }
        }
        break;

      default:
        break;
    }
    return error;
  };

  const validateAndSetError = (name, value) => {
    const error = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    validateAndSetError(name, value);
    if (name === 'enrolledYear' || name === 'passingYear') {
      const other = name === 'enrolledYear' ? 'passingYear' : 'enrolledYear';
      validateAndSetError(other, form[other]);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateAndSetError(name, value);
  };

  const handleCertificateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificate(file);
      validateAndSetError('certificate', file);
    }
  };

  const handleRemoveCertificate = () => {
    setCertificate(null);
    validateAndSetError('certificate', null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getHighSchoolData();
        if (data) {
          const newForm = {
            enrollmentNumber: data.enrollmentNumber || '',
            instituteName: data.instituteName || '',
            qualification: data.qualification || '',
            enrolledYear: data.enrolledYear || '',
            passingYear: data.passingYear || '',
            address: data.address || '',
            city: data.city || '',
            stateRegionProvince: data.stateRegionProvince || '',
            country: data.country || '',
            zipCode: data.zipCode || '',
          };
          setForm(newForm);

          // Validate all loaded fields once
          Object.entries(newForm).forEach(([key, val]) => {
            validateAndSetError(key, val);
          });

          if (data.certificateFileUrl) {
            const fileObj = { url: data.certificateFileUrl, name: getFileNameFromUrl(data.certificateFileUrl) };
            setCertificate(fileObj);
            validateAndSetError('certificate', fileObj); // existing file = valid
          }
        }
      } catch (err) {
        console.error('Error fetching high school data:', err);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async () => {
    const newErrors = {};

    // Validate all text fields
    Object.keys(form).forEach((key) => {
      const error = validateField(key, form[key]);
      if (error) newErrors[key] = error;
    });

    // Validate certificate
    const certError = validateField('certificate', certificate);
    if (certError) newErrors.certificate = certError;

    // Cross-field: enrolled < passing
    if (!newErrors.enrolledYear && !newErrors.passingYear) {
      const enrollVal = getDateValue(form.enrolledYear);
      const passVal = getDateValue(form.passingYear);
      if (enrollVal >= passVal && enrollVal !== 0 && passVal !== 0) {
        newErrors.enrolledYear = 'Enrolled date must be before passing date';
        newErrors.passingYear = 'Passing date must be after enrolled date';
      }
    }

    setValidationErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value) payload.append(key, value);
    });

    if (certificate instanceof File) {
      payload.append('certificateFile', certificate);
    }

    try {
      await saveHighSchoolData(payload);
      setSubmissionResult({
        isSuccess: true,
        title: 'Success',
        message: 'High School details saved successfully!',
      });
      setShowResultPopup(true);
      onNext(form);
    } catch (err) {
      setSubmissionResult({
        isSuccess: false,
        title: 'Submission Error',
        message: err.response?.data?.message || 'Failed to save data.',
      });
      setShowResultPopup(true);
    } finally {
      setLoading(false);
    }
  };


  const closeResultPopup = () => setShowResultPopup(false);
  const triggerCertificateUpload = () => certificateFileRef.current.click();

  return (
    <Box className="stepHighSchoolContainer">
      {loading && (
        <Box className="loading-overlay">
          <Box className="loading-content">
            <CircularProgress size={40} style={{ color: '#4D0054' }} />
            <Typography className="loading-text">Saving...</Typography>
          </Box>
        </Box>
      )}

      <div className="highschool-header">
        <div className="vertical-line"></div>
        <Typography className="stepHighSchoolTitle">🎓 High School Details</Typography>
      </div>

      <Box className="stepHighSchoolGrid">
        <TextField
          label="Enrollment Number / Hall Ticket"
          name="enrollmentNumber"
          value={form.enrollmentNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.enrollmentNumber}
          helperText={validationErrors.enrollmentNumber}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaIdCard color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Institute Name"
          name="instituteName"
          value={form.instituteName}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.instituteName}
          helperText={validationErrors.instituteName}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaUniversity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Qualification"
          name="qualification"
          value={form.qualification}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.qualification}
          helperText={validationErrors.qualification}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaUniversity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="" disabled>-- Please select qualification --</MenuItem>
          {qualifications.map((q) => (
            <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>
          ))}
        </TextField>
<LocalizationProvider dateAdapter={AdapterDayjs}>
  
  {/* Enrolled Year */}
  <DatePicker
    label="Enrolled Year"
    views={["year", "month"]}
    format="MM-YYYY"
    disableFuture
    value={form.enrolledYear ? dayjs(form.enrolledYear, "MM-YYYY") : null}
    onChange={(newValue) =>
      setForm({
        ...form,
        enrolledYear: newValue ? newValue.format("MM-YYYY") : "",
      })
    }
    slotProps={{
      textField: {
        variant: "standard",
        fullWidth: true,
      },
    }}
  />

  {/* Passing Year */}
  <DatePicker
    label="Passing Year"
    views={["year", "month"]}
    format="MM-YYYY"
    disableFuture
    value={form.passingYear ? dayjs(form.passingYear, "MM-YYYY") : null}
    onChange={(newValue) =>
      setForm({
        ...form,
        passingYear: newValue ? newValue.format("MM-YYYY") : "",
      })
    }
    slotProps={{
      textField: {
        variant: "standard",
        fullWidth: true,
      },
    }}
  />

</LocalizationProvider>

        <TextField
          label="Address (optional)"
          name="address"
          value={form.address}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField stepHighSchoolFull"
          fullWidth
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaMapMarkedAlt color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="City"
          name="city"
          value={form.city}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.city}
          helperText={validationErrors.city}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaCity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />


        <IndianStatesSelect
          value={form.stateRegionProvince}
          onChange={handleChange}
          onBlur={handleBlur}
          error={validationErrors.stateRegionProvince}
          helperText={validationErrors.stateRegionProvince}
          className="stepHighSchoolField"
        />

        <TextField
          label="Country"
          name="country"
          value={form.country}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.country}
          helperText={validationErrors.country}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaGlobe color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Zip Code (optional)"
          name="zipCode"
          value={form.zipCode}
          onChange={handleChange}
          onBlur={handleBlur}
          className="stepHighSchoolField"
          fullWidth
          variant="standard"
          error={!!validationErrors.zipCode}
          helperText={validationErrors.zipCode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaEnvelope color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <Box className="upload-container">
          <p className="upload-label-title">
            <FaFileUpload className="filed-icons" /> High School Certificate*
          </p>
          <div className="upload-certificate-group">
            <input
              type="file"
              id="certificateUpload"
              style={{ display: "none" }}
              ref={certificateFileRef}
              onChange={handleCertificateUpload}
              hidden
              accept="image/jpeg,image/png,application/pdf"
            />
            <div onClick={triggerCertificateUpload} className="upload-certificate-button">
              Drag & Drop or Click to Upload
            </div>

            {certificate && (
              <div className="file-list">
                <span className="file-list-item">
                  {certificate instanceof File ? certificate.name : certificate.name || 'Existing File'}
                  <FaTimes className="remove-file-icon" onClick={handleRemoveCertificate} />
                </span>
              </div>
            )}

            {validationErrors.certificate && (
              <Typography color="error" variant="caption">
                {validationErrors.certificate}
              </Typography>
            )}

            <p className="upload-certificate-note">* File must be less than 5MB and must be PDF.</p>
          </div>
        </Box>
      </Box>

      <PopupModel
        show={showResultPopup}
        onClose={closeResultPopup}
        title={submissionResult?.title}
        message={submissionResult?.message}
        isSuccess={submissionResult?.isSuccess}
      />

      <Box className="stepHighSchoolButtonGroup">
        <div className="button-left">
          <button onClick={() => navigate('/candidate/address-info')} className="stepHighSchoolButton secondary">
            <FaBackward style={{ marginRight: '6px' }} /> Previous
          </button>
        </div>

        <div className="button-right">
          <button onClick={handleSubmit} className="stepHighSchoolButton" disabled={loading}>
            Next
          </button>
        </div>
      </Box>
    </Box>
  );
};

export default StepHighSchool;